import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  StarIcon as Star,
  ChevronLeftIcon as ChevronLeft,
  ChevronRightIcon as ChevronRight,
} from "@heroicons/react/24/solid";

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  content: string;
  rating: number;
  location: string;
}

const testimonialsData: Testimonial[] = [
  {
    id: "testimonial1",
    name: "Dre. Zapata",
    role: "Veterinarian",
    company: "Animal Care West",
    content:
      "Vettconsult is a clever and practical tool that cuts down my workload and gives me more time for family life.",
    rating: 5,
    location: "Dorval, Quebec, Canada",
  },
  {
    id: "testimonial2",
    name: "Dr. Abdelkader Mimouni",
    role: "Emergency Veterinarian",
    company: "Gilmour Veterinary Services",
    content:
      "Vettconsult m'a permis de gagner une énorme quantité de temps au quotidien, tout en restant très simple à utiliser. Son interface intuitive et ses fonctionnalités bien conçues en font un outil efficace et accessible.",
    rating: 5,
    location: "Montreal, Canada",
  },
];

const TestimonialsSection: React.FC = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
    }, 9000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextTestimonial = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
  };

  const prevTestimonial = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(
      (prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length
    );
  };

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl font-bold text-[#00342d] mb-6 tracking-tight"
            style={{ fontFamily: "'Bruno Ace', sans-serif" }}
          >
            {t("home.testimonials.title", "What Veterinarians Say")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-600 leading-relaxed"
          >
            {t(
              "home.testimonials.subtitle",
              "Discover how VettConsult is transforming veterinary practice worldwide."
            )}
          </motion.p>
        </div>

        <div className="max-w-4xl mx-auto relative">
          {/* Navigation Buttons */}
          <button
            onClick={prevTestimonial}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 hidden md:flex w-12 h-12 rounded-full bg-white border border-gray-200 items-center justify-center text-gray-400 hover:text-[#00e9be] hover:border-[#00e9be] transition-all duration-300 shadow-sm hover:shadow-md z-20"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextTestimonial}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 hidden md:flex w-12 h-12 rounded-full bg-white border border-gray-200 items-center justify-center text-gray-400 hover:text-[#00e9be] hover:border-[#00e9be] transition-all duration-300 shadow-sm hover:shadow-md z-20"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Card */}
          <div className="relative overflow-hidden min-h-[400px] flex items-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="w-full bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 text-center"
              >
                <div className="flex justify-center gap-1 mb-8">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-[#00e9be]" />
                  ))}
                </div>
                <p className="text-2xl md:text-3xl font-medium text-[#00342d] mb-8 leading-relaxed italic">
                  "{testimonialsData[currentIndex].content}"
                </p>
                <div>
                  <h4 className="text-xl font-bold text-[#00342d]">
                    {testimonialsData[currentIndex].name}
                  </h4>
                  <p className="text-gray-500 font-medium">
                    {testimonialsData[currentIndex].role}
                  </p>
                  <p className="text-[#00e9be] font-semibold text-sm mt-1">
                    {testimonialsData[currentIndex].company}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    {testimonialsData[currentIndex].location}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-3 mt-8">
            {testimonialsData.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentIndex(index);
                }}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex ? "bg-[#00e9be] w-8" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default TestimonialsSection;
